export const ensureNumberOrDefault = (value: unknown, defaultValue: number): number => {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  if (typeof value === 'string') {
    const parsedValue = +value;
    return isNaN(parsedValue) ? defaultValue : parsedValue;
  }

  if (isNaN(value as number)) {
    return defaultValue;
  }

  return typeof value === 'number' ? value : defaultValue;
};

class WithNumber {
  _value: number;
  _decimals: number;
  _fallback: number;

  /**
   * A class that ensures a number value is set, with a fallback option and a specified number of decimal places.
   * @param value - The initial value, can be null or undefined.
   * @param options - An object containing fallback value and optional decimals.
   */
  constructor(value: number, { fallback = 0, decimals = 2 }: { fallback?: number; decimals?: number }) {
    this._decimals = ensureNumberOrDefault(decimals, 2);
    this._fallback = ensureNumberOrDefault(fallback, 0);
    this._value = ensureNumberOrDefault(value, this._fallback);
  }

  add(value: number): WithNumber {
    value = ensureNumberOrDefault(value, 0);
    const result = this._value + value;
    this._value = +result.toFixed(this._decimals);
    return this;
  }

  subtract(value: number): WithNumber {
    value = ensureNumberOrDefault(value, 0);
    const result = this._value - value;
    this._value = +result.toFixed(this._decimals);
    return this;
  }

  multiplyBy(multiplier: number): WithNumber {
    multiplier = ensureNumberOrDefault(multiplier, 0);

    if (this._value === 0 || multiplier === 0 || multiplier === null || multiplier === undefined) {
      this._value = 0;
      return this;
    }

    const result = this._value * multiplier;
    this._value = +result.toFixed(this._decimals);
    return this;
  }

  divideBy(divisor: number): WithNumber {
    divisor = ensureNumberOrDefault(divisor, 0);

    if (this._value === 0 || divisor === 0 || divisor === null || divisor === undefined) {
      this._value = 0;
      return this;
    }

    const result = this._value / divisor;
    this._value = +result.toFixed(this._decimals);
    return this;
  }

  get value(): number {
    return this._value;
  }

  static from(value: number, options?: { fallback?: number; decimals?: number }): WithNumber {
    return new WithNumber(value, options || {});
  }
}

export const withNumber = (value: number, options?: { fallback?: number; decimals?: number }): WithNumber =>
  new WithNumber(value, options || { decimals: 2, fallback: 0 });
